import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Check, ChevronRight, Loader2, CreditCard } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { listPlans, checkoutPlan } from '../lib/api';

const FEATURES = {
  free:   ['720p playback', 'Standard ads', 'Mobile + web'],
  plus:   ['1080p playback', 'No ads', 'Downloads', 'Priority support'],
  pro:    ['Everything in Plus', '4K playback', 'Offline library', 'Early access'],
  studio: ['Everything in Pro', 'Creator analytics', 'Higher payout share', 'Priority moderation'],
};

export default function Memberships() {
  const { user } = useApp();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState('');

  useEffect(() => { listPlans().then(setPlans).catch(() => {}); }, []);

  const choose = async (p) => {
    if (!user) { nav('/signup'); return; }
    if (p.id === 'free') { toast('Free plan is automatic — no checkout needed'); return; }
    setBusy(p.id);
    try {
      const { url } = await checkoutPlan(p.id);
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  const allPlans = [{ id: 'free', name: 'Free', price: 0, tagline: 'For casual viewers' }, ...plans];

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="memberships-page">
      <header className="border-b border-white/5 sticky top-0 z-30 bg-[#0a0a0c]/85 backdrop-blur">
        <div className="max-w-[1280px] mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-2">
            {user ? <Link to="/home" className="btn-ghost">Open app <ChevronRight className="w-4 h-4" /></Link> : <Link to="/login" className="btn-ghost">Sign in</Link>}
          </div>
        </div>
      </header>

      <section className="py-14">
        <div className="max-w-[1280px] mx-auto px-5 text-center">
          <div className="chip">Memberships</div>
          <h1 className="font-display text-[40px] md:text-[60px] leading-[1.05] mt-4">Pick a plan. <span className="brand-gradient">Level up anytime.</span></h1>
          <p className="text-white/65 mt-3 max-w-[680px] mx-auto">Free forever for viewers. Unlock downloads, 4K, and creator tools when you&apos;re ready.</p>
        </div>

        <div className="max-w-[1280px] mx-auto px-5 grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {allPlans.map(p => {
            const current = user?.plan === p.id;
            const highlight = p.id === 'pro';
            return (
              <div key={p.id} className={`card-shell p-6 flex flex-col relative ${highlight ? 'ring-2 ring-[#ff2e63]/50' : ''}`} data-testid={`plan-card-${p.id}`}>
                {highlight && <div className="absolute -top-3 left-6 chip active">Most popular</div>}
                {current && <div className="absolute -top-3 right-6 chip active">Current plan</div>}
                <div className="font-display text-[26px]">{p.name}</div>
                <div className="text-[13px] text-white/55 mt-1">{p.tagline}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-[44px]">${p.price}</span>
                  <span className="text-white/55 text-[13px]">/mo</span>
                </div>
                <ul className="mt-4 space-y-2 text-[13.5px] text-white/80 flex-1">
                  {(FEATURES[p.id] || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="w-4 h-4 text-[#22d3ee] mt-0.5 shrink-0" /><span>{f}</span></li>
                  ))}
                </ul>
                <button disabled={current || busy === p.id} onClick={() => choose(p)} className={`mt-5 w-full ${current ? 'btn-ghost opacity-60' : (highlight ? 'btn-primary' : 'btn-ghost')} disabled:opacity-50`} data-testid={`choose-plan-${p.id}`}>
                  {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (current ? 'Active' : (p.price === 0 ? 'Start free' : <><CreditCard className="w-4 h-4" /> Choose {p.name}</>))}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[12px] text-white/45 mt-8 max-w-[600px] mx-auto">Secure payment by Stripe. Cancel anytime from your account. All prices in USD. You&apos;ll be redirected to Stripe Checkout.</p>
      </section>
    </div>
  );
}
